import type { Cell, Color, Piece } from './types';

// Кампанийные сцены-посадки под рекламу. block-blast эндлесс — «победы» нет, поэтому
// крючок A = доска в одном ходу от ЖИРНОГО клира (4–5 линий сразу): нужная фигура уже
// в трее, игрок роняет её и ловит взрыв. C — «партия в процессе» (продолжай строить).
// Проверять вручную через /block-blast/?scene=<id>: заявленный дроп реально даёт клир.
//
// URL объявления: /block-blast/?scene=<id>&utm_source=<net>&utm_campaign=<c>&utm_content=<creative>

type HeadlineKey = 'hook_bb_fivelines' | 'hook_bb_fourlines' | 'hook_bb_midgame';

export interface Scene {
  build: () => { grid: (Color | null)[][]; tray: [Piece, Piece, Piece] };
  headlineKey: HeadlineKey;
}

const PAL: Color[] = ['red', 'orange', 'yellow', 'green', 'blue', 'purple', 'cyan'];

function emptyGrid(): (Color | null)[][] {
  return Array.from({ length: 8 }, () => Array<Color | null>(8).fill(null));
}

// Заполнить прямоугольник [r0..r1]×[c0..c1] (включительно) цветами по шахматному узору.
// Цвет не влияет на логику клира — только на вид.
function fill(g: (Color | null)[][], r0: number, r1: number, c0: number, c1: number): void {
  for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) g[r][c] = PAL[(r + c) % PAL.length];
}

function piece(coords: [number, number][], color: Color): Piece {
  const cells: Cell[] = coords.map(([r, c]) => ({ r, c }));
  const rows = Math.max(...cells.map(x => x.r)) + 1;
  const cols = Math.max(...cells.map(x => x.c)) + 1;
  return { cells, color, rows, cols };
}

const I5 = (color: Color): Piece => piece([[0, 0], [1, 0], [2, 0], [3, 0], [4, 0]], color); // вертикаль 5×1
const I4 = (color: Color): Piece => piece([[0, 0], [1, 0], [2, 0], [3, 0]], color);          // вертикаль 4×1
const SQ = (color: Color): Piece => piece([[0, 0], [0, 1], [1, 0], [1, 1]], color);          // 2×2
const L4 = (color: Color): Piece => piece([[0, 0], [1, 0], [2, 0], [2, 1]], color);          // L

export const SCENES: Record<string, Scene> = {
  // A: пять рядов заполнены во всех столбцах кроме 7-го → вертикальный I5 в столбец 7
  //    (строки 0–4) закрывает 5 линий разом.
  'fivelines-a': {
    headlineKey: 'hook_bb_fivelines',
    build: () => {
      const g = emptyGrid();
      fill(g, 0, 4, 0, 6);   // строки 0–4, столбцы 0–6; столбец 7 пуст
      fill(g, 7, 7, 0, 3);   // декоративный кусок снизу (не образует полную линию)
      return { grid: g, tray: [I5('cyan'), SQ('purple'), L4('orange')] };
    },
  },

  // A: четыре ряда без 7-го столбца → вертикальный I4 в столбец 7 закрывает 4 линии.
  'fourlines-b': {
    headlineKey: 'hook_bb_fourlines',
    build: () => {
      const g = emptyGrid();
      fill(g, 0, 3, 0, 6);   // строки 0–3, столбцы 0–6; столбец 7 пуст
      fill(g, 6, 7, 1, 5);   // декоративный блок снизу
      return { grid: g, tray: [I4('green'), SQ('red'), L4('blue')] };
    },
  },

  // C: умеренно заполненная играбельная доска без полных линий — «продолжай строить».
  'midgame-c': {
    headlineKey: 'hook_bb_midgame',
    build: () => {
      const g = emptyGrid();
      fill(g, 5, 7, 0, 2);   // нижний-левый блок 3×3
      fill(g, 0, 1, 6, 7);   // верхний-правый 2×2
      fill(g, 3, 4, 3, 4);   // центральный 2×2
      return { grid: g, tray: [L4('yellow'), SQ('cyan'), piece([[0, 0], [0, 1], [0, 2]], 'purple')] };
    },
  },
};
